import React from "react";
import { Plus, Trash2, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

/* =============================================================================
 * CategorySheet — Excel-like editable table for a finance category.
 *  - Inline cell editing (click to edit, Enter to save, Esc to cancel)
 *  - Add row at the bottom (visible always when editable)
 *  - Delete row on hover
 *  - Columns are configurable per category
 * ========================================================================== */

const COL_DEFS = {
  // key → { label, type, width, format?, parse? }
  label:    { label: "Item",        type: "text",   width: "minmax(200px, 1.4fr)", required: true, placeholder: "Nome" },
  amount:   { label: "Atual",       type: "number", width: "minmax(110px, 1fr)",  align: "right", suffix: "€" },
  target:   { label: "Alvo",        type: "number", width: "minmax(110px, 1fr)",  align: "right", suffix: "€" },
  monthly:  { label: "Mensal",      type: "number", width: "minmax(110px, 1fr)",  align: "right", suffix: "€" },
  progress: { label: "Progresso",   type: "number", width: "minmax(110px, 1fr)",  align: "right", suffix: "%" },
  due_date: { label: "Data",        type: "date",   width: "minmax(120px, 1fr)" },
  note:     { label: "Nota",        type: "text",   width: "minmax(160px, 1.6fr)" },
};

const fmt = (val, def) => {
  if (val === null || val === undefined || val === "") return "";
  if (def.type === "number") return new Intl.NumberFormat("de-DE").format(val);
  return val;
};

/* Single editable cell */
const Cell = React.memo(function Cell({ value, def, onSave, testId }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? "");
  const ref = React.useRef(null);

  React.useEffect(() => { setDraft(value ?? ""); }, [value]);
  React.useEffect(() => { if (editing) setTimeout(() => ref.current?.focus(), 30); }, [editing]);

  const commit = async () => {
    const raw = draft;
    let next;
    if (def.type === "number") next = raw === "" ? null : Number(raw);
    else next = raw === "" ? null : raw;
    if (next === value) { setEditing(false); return; }
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar.");
    }
  };

  if (editing) {
    return (
      <input
        ref={ref}
        type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
        inputMode={def.type === "number" ? "decimal" : undefined}
        step={def.type === "number" ? "0.01" : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { setDraft(value ?? ""); setEditing(false); }
        }}
        onBlur={commit}
        className={`w-full bg-black/60 border border-hx-neon/40 rounded-sm px-2 py-1 text-[12px] text-hx-text outline-none font-mono ${def.align === "right" ? "text-right" : ""}`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={testId}
      className={`w-full text-[12px] px-2 py-1 rounded-sm hover:bg-hx-neon/[0.05] transition-colors cursor-pointer min-h-[26px] ${
        def.align === "right" ? "text-right" : "text-left"
      } ${value == null || value === "" ? "text-hx-muted/40" : "text-hx-text"}`}
      title="Clique para editar"
    >
      {value == null || value === "" ? "—" : fmt(value, def)}
      {def.suffix && value != null && value !== "" && (
        <span className="text-hx-muted ml-1 text-[10px]">{def.suffix}</span>
      )}
    </button>
  );
});

/* New-row form rendered inline at the bottom of the table */
const NewRow = ({ columns, onSubmit, testId }) => {
  const [form, setForm] = React.useState({});
  const [busy, setBusy] = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.label) { toast.error("Item é obrigatório"); return; }
    setBusy(true);
    try {
      const payload = {};
      for (const k of columns) {
        const def = COL_DEFS[k];
        const v = form[k];
        if (v === undefined || v === "" || v === null) continue;
        payload[k] = def.type === "number" ? Number(v) : v;
      }
      await onSubmit(payload);
      setForm({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro.");
    } finally { setBusy(false); }
  };

  return (
    <form
      onSubmit={submit}
      className="grid items-center gap-2 px-3 py-2 border-t border-dashed border-white/[0.10] bg-black/30"
      style={{ gridTemplateColumns: `${columns.map((c) => COL_DEFS[c].width).join(" ")} 60px` }}
      data-testid={testId ? `${testId}-newrow` : undefined}
    >
      {columns.map((k) => {
        const def = COL_DEFS[k];
        return (
          <input
            key={k}
            type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
            placeholder={def.placeholder || def.label}
            value={form[k] ?? ""}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className={`bg-black/50 border border-white/[0.10] focus:border-hx-neon/40 rounded-sm px-2 py-1.5 text-[12px] text-hx-text outline-none transition-colors font-mono placeholder:text-hx-muted/40 ${def.align === "right" ? "text-right" : ""}`}
            data-testid={testId ? `${testId}-newrow-${k}` : undefined}
          />
        );
      })}
      <button
        type="submit"
        disabled={busy}
        data-testid={testId ? `${testId}-newrow-add` : undefined}
        className="flex items-center justify-center px-2 py-1.5 rounded-sm bg-hx-neon/15 border border-hx-neon/40 text-hx-neon hover:bg-hx-neon/25 transition-colors disabled:opacity-50"
        title="Adicionar linha"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} strokeWidth={2.2} />}
      </button>
    </form>
  );
};

/* Memoized row — onSave is stabilised via useMemo so Cell.memo blocks re-renders */
const SheetRow = React.memo(function SheetRow({ entry, columns, category, headerGrid, onUpdate, onDelete }) {
  const onSaveByCol = React.useMemo(() => {
    const m = {};
    for (const c of columns) {
      m[c] = (v) => onUpdate(entry.id, { [c]: v });
    }
    return m;
  }, [columns, entry.id, onUpdate]);
  return (
    <div
      className="grid gap-2 items-center px-3 py-1 group hover:bg-white/[0.03] transition-colors"
      style={{ gridTemplateColumns: headerGrid }}
      data-testid={`sheet-row-${entry.id}`}
    >
      {columns.map((c) => (
        <Cell
          key={c}
          value={entry[c]}
          def={COL_DEFS[c]}
          onSave={onSaveByCol[c]}
          testId={`sheet-cell-${category}-${c}-${entry.id}`}
        />
      ))}
      <button
        onClick={() => onDelete(entry.id)}
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-hx-muted hover:text-red-300 hover:bg-red-500/10 transition-all justify-self-center"
        title="Eliminar linha"
        data-testid={`sheet-del-${entry.id}`}
      >
        <Trash2 size={11} strokeWidth={2} />
      </button>
    </div>
  );
});

export const CategorySheet = ({
  title,
  icon: Icon,
  columns = ["label", "amount", "target", "monthly", "due_date", "note"],
  entries,
  category,
  onCreate,
  onUpdate,
  onDelete,
  description,
}) => {
  const headerGrid = `${columns.map((c) => COL_DEFS[c].width).join(" ")} 60px`;

  // Aggregate totals for numeric columns
  const totals = React.useMemo(() => {
    const t = {};
    for (const c of columns) {
      if (COL_DEFS[c].type !== "number" || c === "progress") continue;
      t[c] = entries.reduce((s, e) => s + (e[c] || 0), 0);
    }
    return t;
  }, [entries, columns]);

  return (
    <div data-testid={`sheet-${category}`}>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-md hx-metal flex items-center justify-center shrink-0">
          {Icon && <Icon size={18} className="text-hx-neon" strokeWidth={1.6} />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-2xl lg:text-3xl tracking-tight text-hx-text">{title}</h1>
          {description && <p className="mt-1 text-[13px] text-hx-muted leading-relaxed max-w-2xl">{description}</p>}
        </div>
        <div className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-hx-muted">
          <div>{entries.length} {entries.length === 1 ? "registo" : "registos"}</div>
        </div>
      </div>

      <div
        className="rounded-lg border border-white/[0.10] overflow-hidden"
        style={{ background: "linear-gradient(180deg, rgba(20,26,34,0.55) 0%, rgba(10,15,26,0.90) 100%)" }}
      >
        {/* Header */}
        <div
          className="grid gap-2 px-3 py-2 border-b border-white/[0.10] bg-black/40 text-[9px] uppercase tracking-[0.22em] text-hx-muted font-semibold"
          style={{ gridTemplateColumns: headerGrid }}
        >
          {columns.map((c) => (
            <div key={c} className={COL_DEFS[c].align === "right" ? "text-right pr-2" : "pl-2"}>
              {COL_DEFS[c].label}
            </div>
          ))}
          <div className="text-center">—</div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-white/[0.04]">
          {entries.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12px] text-hx-muted/70 italic">
              Sem registos. Adiciona o primeiro abaixo.
            </div>
          ) : entries.map((e) => (
            <SheetRow
              key={e.id}
              entry={e}
              columns={columns}
              category={category}
              headerGrid={headerGrid}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </div>

        {/* Totals */}
        {Object.keys(totals).length > 0 && entries.length > 0 && (
          <div
            className="grid gap-2 px-3 py-2 border-t border-white/[0.10] bg-black/40 text-[11px] font-mono tabular-nums"
            style={{ gridTemplateColumns: headerGrid }}
          >
            {columns.map((c, i) => (
              <div key={c} className={`${COL_DEFS[c].align === "right" ? "text-right pr-2" : "pl-2"}`}>
                {i === 0 ? (
                  <span className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-semibold">Total</span>
                ) : totals[c] !== undefined ? (
                  <span className="text-hx-neon">{new Intl.NumberFormat("de-DE").format(totals[c])} {COL_DEFS[c].suffix || ""}</span>
                ) : ""}
              </div>
            ))}
            <div />
          </div>
        )}

        {/* New row form */}
        <NewRow
          columns={columns}
          testId={`sheet-${category}`}
          onSubmit={(payload) => onCreate({ category, ...payload })}
        />
      </div>

      <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-hx-muted/60">
        Clique em qualquer célula para editar · Hover na linha para eliminar · Enter para guardar · Esc para cancelar
      </p>
    </div>
  );
};

export { Plus, Trash2, Loader2, Check, X };
